import { useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'
import { resetPassword, type ResetPasswordOutput } from 'aws-amplify/auth'
import useAuthStore from '@/auth/authStore'
import { useSignOut } from '@/auth/useSignOut'
import type { User } from '@/common/types'

export function usePasswordResetFlow(user: User | null) {
    const setUsername = useAuthStore((state) => state.setUsername)
    const navigate = useNavigate()
    const signOut = useSignOut()

    const [isPasswordChangeOpen, setIsPasswordChangeOpen] = useState(false)
    const [isResettingPassword, setIsResettingPassword] = useState(false)
    const [sendingConfirmation, setSendingConfirmation] = useState(false)

    const openPasswordChange = () => setIsPasswordChangeOpen(true)
    const closePasswordChange = () => setIsPasswordChangeOpen(false)
    const closePasswordReset = () => setIsResettingPassword(false)

    function handleResetPasswordNextSteps(output: ResetPasswordOutput) {
        const { nextStep } = output
        const codeDeliveryDetails = nextStep.codeDeliveryDetails
        switch (nextStep.resetPasswordStep) {
            case 'CONFIRM_RESET_PASSWORD_WITH_CODE':
                toast.success(`Confirmation code was sent to ${codeDeliveryDetails.deliveryMedium?.toLowerCase()}`)
                // Collect the confirmation code from the user and pass to confirmResetPassword.
                break
            case 'DONE':
                break
        }
    }

    const openResetPasswordForm = async () => {
        if (!user || !user.emailId) {
            toast.error('Email ID does not exist')
            return
        }
        setUsername(user.emailId)
        setSendingConfirmation(true)
        const output = await resetPassword({ username: user.emailId })
        handleResetPasswordNextSteps(output)
        setSendingConfirmation(false)
        setIsPasswordChangeOpen(false)
        setIsResettingPassword(true)
    }

    async function handleReset() {
        await signOut({ redirect: true })
        toast.success('You have reset the password successfully. Please login again!')
        navigate('/')
    }

    return {
        isPasswordChangeOpen,
        isResettingPassword,
        sendingConfirmation,
        openPasswordChange,
        closePasswordChange,
        closePasswordReset,
        openResetPasswordForm,
        handleReset,
    }
}
